import { ParentProps, Show, createEffect, createMemo, createSignal, on, onMount } from 'solid-js'
import { StaffRole, roleDashboard as api } from 'sis-app/api'
import { Flexbox, Focus, ViewContextJSX, ViewContext, requireStepContext, requireWizardContext, requireDataStoreContext, Loader, Alert } from 'st-app'
import { RoleDashboardContext, RoleDashboardContextJsx, requireRoleDashboardContext } from './RoleDashboardContext'
import { RegistrarDashboard } from './partials/RegistrarDashboard'
import { Header } from './partials/Header'
import { animate } from 'common'
import { NurseDashboard } from './partials/NurseDashboard'
import { SchoolAdminDashboard } from './partials/SchoolAdminDashboard'
import { AttendanceClerkDashboard } from './partials/AttendanceClerkDashboard'
import { DistrictAdminDashboard } from './partials/DistrictAdminDashboard'
import { CounselorDashboard } from './partials/CounselorDashboard'
import { DistrictStateReportingDashboard } from './partials/DistrictStateReportingDashboard'
import { SupportAgentDashboard } from './partials/SupportAgentDashboard'
import { SupportSupervisorDashboard } from './partials/SupportSupervisorDashboard'
import { SupportUserDashboard } from './partials/SupportUserDashboard'
import { StudentSelectionModal } from './partials/StudentSelectionModal'
import styles from './sis-role-dashboard.module.css'
import { LocateStudentModal } from './partials/LocateStudentModal'
import { QuickRefGuideModal } from './partials/QuickRefGuideModal'
import { ViewDrawer } from './partials/ViewDrawer'

export class RoleDashboardProps { }

export default function RoleDashboard(props: ParentProps<RoleDashboardProps>) {
    const context = new RoleDashboardContext();

    return (
        <RoleDashboardContextJsx.Provider value={context}>
            <Focus>
                <ViewContextJSX.Provider value={new ViewContext()}>
                    <RoleDashboardInternal {...props} />
                </ViewContextJSX.Provider>
            </Focus>
        </RoleDashboardContextJsx.Provider>
    )
}

function RoleDashboardInternal(props: ParentProps<RoleDashboardProps>) {
    const ctx = requireRoleDashboardContext();
    const [roleElements, setRoleElements] = createSignal<Record<any, HTMLDivElement>>({});

    function addRoleElement(role: StaffRole, el: HTMLElement) {
        setRoleElements({ ...roleElements(), [role]: el });

        if (ctx.selectedRole() === role && ctx.configuration()?.userRoles.includes(role)) {
            requestAnimationFrame(() => {
                el.style.display = '';
                el.style.opacity = '1';
            });
        }
    }

    onMount(() => {
        createEffect(on(
            () => ctx.selectedRole(),
            (role, prevRole) => {
                if (prevRole) {
                    const prev = roleElements()[prevRole];
                    const next = roleElements()[role];

                    if (prev) {
                        animate(prev, 250, { opacity: '1' }, { opacity: '0' })
                            .then(() => prev.style.display = 'none');
                    }

                    if (next) {
                        slideIn(next);
                    }
                }
            }
        ));
    });

    return <Show when={!!ctx.configuration()}>
        <Flexbox auto dir='vertical' class={styles['role-dashboard']}>
            <Header />
            <Flexbox.Item auto style="overflow-y: auto;">
                <Flexbox.Item auto ref={(el) => addRoleElement(StaffRole.AttendanceClerk, el)} style="display: none">
                    <AttendanceClerkDashboard />
                </Flexbox.Item>
                <Flexbox.Item auto ref={(el) => addRoleElement(StaffRole.Counselor, el)} style="display: none">
                    <CounselorDashboard />
                </Flexbox.Item>
                <Flexbox.Item auto ref={(el) => addRoleElement(StaffRole.DistrictAdmin, el)} style="display: none">
                    <DistrictAdminDashboard />
                </Flexbox.Item>
                <Flexbox.Item auto ref={(el) => addRoleElement(StaffRole.DistrictStateReporting, el)} style="display: none">
                    <DistrictStateReportingDashboard />
                </Flexbox.Item>
                <Flexbox.Item auto ref={(el) => addRoleElement(StaffRole.Nurse, el)} style="display: none">
                    <NurseDashboard />
                </Flexbox.Item>
                <Flexbox.Item auto ref={(el) => addRoleElement(StaffRole.Registrar, el)} style="display: none">
                    <RegistrarDashboard />
                </Flexbox.Item>
                <Flexbox.Item auto ref={(el) => addRoleElement(StaffRole.SchoolAdministrator, el)} style="display: none">
                    <SchoolAdminDashboard />
                </Flexbox.Item>
                <Flexbox.Item auto ref={(el) => addRoleElement(StaffRole.SupportAgent, el)} style="display: none">
                    <SupportAgentDashboard />
                </Flexbox.Item>
                <Flexbox.Item auto ref={(el) => addRoleElement(StaffRole.SupportSupervisor, el)} style="display: none">
                    <SupportSupervisorDashboard />
                </Flexbox.Item>
                <Flexbox.Item auto ref={(el) => addRoleElement(StaffRole.SupportUser, el)} style="display: none">
                    <SupportUserDashboard />
                </Flexbox.Item>
            </Flexbox.Item>
        </Flexbox>

        <ViewDrawer />

        <Show when={ctx.showLocateStudentModal()}>
            <LocateStudentModal />
        </Show>

        <Show when={ctx.showQuickRefGuides()}>
            <QuickRefGuideModal />
        </Show>
    </Show>
}

RoleDashboard.defaultProps = new RoleDashboardProps();

function slideIn(el: HTMLDivElement) {
    requestAnimationFrame(() => {
        const parentRect = el.parentElement!.getBoundingClientRect();

        animate(el, 250,
            {
                display: '',
                opacity: '1',
                position: 'fixed',
                left: `${parentRect.left}px`,
                top: `${parentRect.bottom}px`,
                width: `${parentRect.width}px`,
            },
            {
                top: `${parentRect.top}px`,
            }
        ).then(() => {
            el.style.position = '';
            el.style.left = '';
            el.style.top = '';
            el.style.width = '';
        });
    });
}
